import { differenceInMinutes } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import type { BulkAppointment } from "../types";

// Athena returns appointment times as bare wall-clock strings (no offset) local
// to the clinic. We anchor them to this zone to get absolute instants, then
// compare to the browser's clock and display in the browser's local timezone.
// TODO(revisit later): assumes a single clinic zone (verified "fine for now").
// Multi-timezone departments need a per-department source zone — see
// docs/codebase_knowledge.md "Deferred work".
export const CLINIC_TZ = "America/New_York";
export const DEFAULT_DURATION_MIN = 15;

export interface EnrichedAppointment {
  appt: BulkAppointment;
  patientId: string | null;
  encounterId?: string;
  /** The display name, falling back to patientId or "Unknown patient" if absent. */
  name: string;
  apptType: string;
  start: Date | null;
  end: Date | null;
}

/** A schedule entry as returned by the backend `GET /api/home/schedule`. */
export interface ScheduleAppointment {
  id: string;
  patientName: string;
  patientId: string;
  /** A 12-hour wall-clock label, e.g. "09:00 AM". */
  time: string;
  type: string;
}

/**
 * Anchors a 12-hour wall-clock label ("hh:mm AM/PM") to *today* in CLINIC_TZ.
 *
 * The home schedule is "today's appointments", so the date component is the
 * current calendar day in the clinic zone; only the time is supplied by the
 * backend. Returns null for an unparseable label so the row still renders.
 *
 * The backend types `time` as a string, but the underlying schedule source can
 * deliver a missing/non-string value (e.g. a null or numeric time) that the JSON
 * type doesn't capture, so we coerce defensively before parsing — a non-string
 * simply yields an unparseable label (null) and the row still renders, rather
 * than throwing `time.trim is not a function`.
 *
 * @param time - The 12-hour label, e.g. "09:00 AM".
 * @param today - The reference instant whose clinic-local date anchors the time.
 * @returns The absolute instant, or null when the label cannot be parsed.
 */
export const scheduleInstant = (time: string, today: Date): Date | null => {
  const label = typeof time === "string" ? time : "";
  const match = label.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const [, hhRaw, min, meridiem] = match;
  let hh = Number(hhRaw) % 12;
  if (meridiem.toUpperCase() === "PM") hh += 12;
  // Resolve the clinic-local calendar date for `today` (so "today" is correct
  // even when the browser zone differs from the clinic zone).
  const [m, d, y] = today
    .toLocaleDateString("en-US", {
      timeZone: CLINIC_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/");
  const wall = `${y}-${m}-${d}T${String(hh).padStart(2, "0")}:${min}:00`;
  const instant = fromZonedTime(wall, CLINIC_TZ);
  // Defensive: once the regex above matches, the assembled wall-clock is always
  // a valid date, so the NaN branch is unreachable in practice.
  /* v8 ignore next */
  return Number.isNaN(instant.getTime()) ? null : instant;
};

/**
 * Maps the backend home-schedule shape into the enriched appointments the
 * schedule/side columns render. The backend supplies a display name (falling
 * back to the patient id or "Unknown patient" if absent), patient
 * id, a 12-hour time label, and a visit type directly, so no Athena enrichment
 * is needed.
 *
 * @param appointments - The raw schedule entries from `GET /api/home/schedule`.
 * @param today - The reference instant anchoring each entry's time to a date.
 * @returns The enriched appointments, ascending by start instant.
 */
export const mapScheduleToEnriched = (
  appointments: ScheduleAppointment[],
  today: Date,
): EnrichedAppointment[] =>
  appointments
    .map((appt): EnrichedAppointment => {
      const start = scheduleInstant(appt.time, today);
      const end = start
        ? new Date(start.getTime() + DEFAULT_DURATION_MIN * 60_000)
        : null;
      return {
        appt: { appointmentid: appt.id, patientid: appt.patientId },
        patientId: appt.patientId || null,
        name: appt.patientName || appt.patientId || "Unknown patient",
        apptType: appt.type || "Appointment",
        start,
        end,
      };
    })
    .sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0));

/** Formats an absolute instant as a time in the browser's local timezone. */
export const formatLocalTime = (d: Date): string =>
  d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/** Human countdown from now to start, e.g. "in 25 min" / "in 1h 10m". */
export const formatCountdown = (start: Date, now: Date): string => {
  const mins = Math.max(0, differenceInMinutes(start, now));
  if (mins < 60) return `in ${mins} min`;
  return `in ${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/**
 * Splits enriched appointments into the upcoming list (current + future) and the
 * recent list (already completed). An appointment is "recent" once its end
 * instant has passed `now`; the in-progress one (now ∈ [start, end)) and anything
 * with unparseable times stay in `upcoming` so they're never hidden.
 *
 * `upcoming` preserves the incoming (ascending) order; `recent` is sorted
 * most-recent-first.
 */
export const splitSchedule = (
  enriched: EnrichedAppointment[],
  now: Date,
): { upcoming: EnrichedAppointment[]; recent: EnrichedAppointment[] } => {
  const upcoming: EnrichedAppointment[] = [];
  const recent: EnrichedAppointment[] = [];
  for (const item of enriched) {
    if (item.end && item.end <= now) {
      recent.push(item);
    } else {
      upcoming.push(item);
    }
  }
  recent.sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0));
  return { upcoming, recent };
};
